import React, { useState, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import moment from 'moment';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { useManualState } from './Provider';
import * as action from './store/Document.action';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import ShareDocumentModal from './ShareDocumentModal';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import PreviewModal from '../../Components/ModalPopup/PreviewModal';
import { shortenandHoverString } from '../../Components/helper';
import { DownloadIcon, PreviewIcon, EditIcon, ArchiveIcon, DeleteIcon, UpdateIcon, ShareIcon, MoveIcon, CloseIcon, MoreIcon } from '../../bombiix-icon';

const ItemTypes = {
  DOCUMENT: 'document',
};

function DocumentRow({
  document,
  onDropDocument,
  onCheck,
  handleEdit,
  handleDownload,
  handleDeleteMultiple,
  handleFolderDownload,
  handleArchiveCard,
  onPreview,
  onRenew,
  shareDocumentWithSupplier,
  moveDocumentToFolder,
  setSelectedFolder,
  isSupplierLoggedIn,
  manualAddEditAccess,
  manualDeleteAccess,
  state,
  isArchived,
  getAllFolderList,
  i,
  isSubBrandAccess,
}) {
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const { t } = useTranslation('common');
  const handleToggle = (rowId, nextShow) => {
    setOpenPopoverId((currentId) => {
      // If closing the current popover, set to null
      if (!nextShow && currentId === rowId) {
        return null;
      }
      // If opening a new popover, set its ID
      if (nextShow) {
        return rowId;
      }
      return currentId; // No change for other scenarios
    });
  };

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.DOCUMENT,
    item: { id: document._id, type: document.type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));
  return (
    <tr ref={drag} style={{ opacity: isDragging ? 0.5 : 1 }} key={document._id}>
      <td>
        <div className="common-checkbox no-label margin-auto">
          <input
            onChange={(e) => {
              onCheck(e, document?._id);
            }}
            name={`check_${i}`}
            id={`check_${i}`}
            type="checkbox"
            checked={state?.selectedIds?.includes(document?._id)}
          />
          <label htmlFor={`check_${i}`} className="chklabel" />
        </div>
      </td>
      <td className="cursor-pointer" onClick={() => document.type === 'folder' && onDropDocument(document)}>
        <div className="d-flex align-items-center">
          <i className={document.type === 'folder' ? 'icon-Folder f-24 mr-8' : 'icon-File f-24 mr-8'} />{' '}
          {shortenandHoverString(document.documentTitle, 30, 'auto')}
        </div>
      </td>
      <td>{document.size}</td>
      <td>{document.documentType?.name}</td>
      <td>{document.brand?.name || document.brand}</td>
      {isSubBrandAccess && <td>{document.subBrand?.name || document.subBrand}</td>}
      <td>{shortenandHoverString(document?.suppliers?.map((supplier) => supplier.firstName).join(', '), 30, 'auto')}</td>
      <td>{document.expiredAt ? moment(document.expiredAt).format('DD-MM-YYYY') : ''}</td>
      <td>
        <div className="manual-actions">
          {isSupplierLoggedIn ? (
            <>
              <button className="btn" type="button" aria-label="button" onClick={() => handleDownload(document)}>
                <DownloadIcon size={28} />
              </button>

              <button className="btn" onClick={() => onPreview(document)} type="button" aria-label="button">
                <PreviewIcon size={24} />
              </button>
            </>
          ) : (
            <>
              {!isArchived && manualAddEditAccess && (
                <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.EDIT')}>
                  <button type="button" aria-label="Edit" className="btn p-0" onClick={() => handleEdit(document)}>
                    <EditIcon size={24} />
                  </button>
                </CustomTooltip>
              )}
              {manualAddEditAccess && isArchived && (
                <CustomTooltip placement="top" tooltipText={state?.isArchived ? t('MANUAL_DOCUMENTS.UNARCHIVE') : t('MANUAL_DOCUMENTS.ARCHIVE')}>
                  <button type="button" aria-label="archive" className="btn p-0" onClick={() => handleArchiveCard(document?._id)}>
                    <ArchiveIcon size={24} />{' '}
                  </button>
                </CustomTooltip>
              )}
              {manualDeleteAccess && (
                <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.DELETE')}>
                  <button aria-label="button" type="button" className="btn p-0" onClick={() => handleDeleteMultiple(document?._id)}>
                    <DeleteIcon size={24} />
                  </button>
                </CustomTooltip>
              )}

              <button aria-label="more" type="button" className="btn p-0">
                <OverlayTrigger
                  placement="left"
                  trigger="click"
                  rootClose
                  onToggle={(nextShow) => handleToggle(i, nextShow)}
                  overlay={
                    <Popover className="table-action-popup">
                      <Popover.Content>
                        <ul>
                          {document?.type === 'file' && (
                            <>
                              {manualAddEditAccess && !isArchived && (
                                <li>
                                  <button type="button" aria-label="archive" className="btn" onClick={() => handleArchiveCard(document?._id)}>
                                    <ArchiveIcon size={24} />{' '}
                                    <span className="text">{state?.isArchived ? t('MANUAL_DOCUMENTS.UNARCHIVE') : t('MANUAL_DOCUMENTS.ARCHIVE')}</span>
                                  </button>
                                </li>
                              )}
                              {!isArchived && manualAddEditAccess && (
                                <li>
                                  <button className="btn" type="button" aria-label="button" onClick={() => onRenew(document)}>
                                    <UpdateIcon size={28} /> <span className="text">{t('MANUAL_DOCUMENTS.RENEW')}</span>
                                  </button>
                                </li>
                              )}

                              <li>
                                <button className="btn" onClick={() => onPreview(document)} type="button" aria-label="button">
                                  <PreviewIcon size={24} /> <span className="text">{t('MANUAL_DOCUMENTS.PREVIEW')}</span>
                                </button>
                              </li>
                            </>
                          )}
                          <li>
                            <button className="btn" type="button" aria-label="button" onClick={() => handleFolderDownload(document)}>
                              <DownloadIcon size={28} /> <span className="text">{t('MANUAL_DOCUMENTS.DOWNLOAD')}</span>
                            </button>
                          </li>
                          {!isArchived && manualAddEditAccess && (
                            <>
                              {document?.type === 'file' && (
                                <li>
                                  <button aria-label="button" type="button" className="btn" onClick={() => shareDocumentWithSupplier(document)}>
                                    <ShareIcon size={28} /> <span className="text">{t('MANUAL_DOCUMENTS.SHARE')}</span>
                                  </button>
                                </li>
                              )}
                              <li>
                                {' '}
                                <button aria-label="Move Document" type="button" className="btn d-inline-flex" onClick={() => moveDocumentToFolder(document)}>
                                  <OverlayTrigger
                                    placement="top"
                                    trigger="click"
                                    rootClose
                                    overlay={
                                      <Popover className="custom-popover file-download-popover">
                                        <Popover.Content>
                                          <div className="mb-0 common-react-select" onClick={(e) => e.stopPropagation()}>
                                            <Select
                                              id="folderSelect"
                                              className="common-select"
                                              classNamePrefix="common-select"
                                              options={[
                                                ...(state?.breadCrumbData?.length > 0 ? [{ value: 'home', label: 'Home' }] : []),
                                                ...getAllFolderList
                                                  .filter((re) => re?._id !== document?._id)
                                                  .map((folder) => ({
                                                    value: folder._id,
                                                    label: folder.documentTitle,
                                                  })),
                                              ]}
                                              value={
                                                state?.selectedFolder
                                                  ? {
                                                      value: state?.selectedFolder,
                                                      label:
                                                        state?.selectedFolder === 'home'
                                                          ? 'Home'
                                                          : getAllFolderList.find((folder) => folder._id === state?.selectedFolder)?.documentTitle,
                                                    }
                                                  : null
                                              }
                                              onChange={(selectedOption) => setSelectedFolder(selectedOption)}
                                              placeholder="Select Folder"
                                            />
                                          </div>
                                        </Popover.Content>
                                      </Popover>
                                    }
                                  >
                                    <div>
                                      <MoveIcon size={28} />
                                      {t('MANUAL_DOCUMENTS.MOVE')}
                                    </div>
                                  </OverlayTrigger>
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      </Popover.Content>
                    </Popover>
                  }
                >
                  <span className="d-inline-flex align-items-center">{openPopoverId === i ? <CloseIcon size={24} /> : <MoreIcon size={24} />}</span>
                </OverlayTrigger>
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function FolderRow({
  folder,
  onDropDocument,
  enterFolder,
  state,
  i,
  onCheck,
  manualAddEditAccess,
  manualDeleteAccess,
  isArchived,
  getAllFolderList,
  handleFolderDownload,
  shareDocumentWithSupplier,
  moveDocumentToFolder,
  handleEdit,
  handleDeleteMultiple,
  setSelectedFolder,
  isSubBrandAccess,
}) {
  const [, dropRef] = useDrop(() => ({
    accept: ItemTypes.DOCUMENT,
    drop: (item) => onDropDocument(item, folder),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const [openPopoverId, setOpenPopoverId] = useState(null);

  const { t } = useTranslation('common');
  const handleToggle = (rowId, nextShow) => {
    setOpenPopoverId((currentId) => {
      if (!nextShow && currentId === rowId) {
        return null;
      }

      if (nextShow) {
        return rowId;
      }
      return currentId;
    });
  };

  const [{ opacity }, dragRef] = useDrag(
    () => ({
      type: ItemTypes.DOCUMENT,
      item: { id: folder._id, type: folder.type },

      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
      }),
    }),
    [],
  );

  const combinedRef = useRef();
  const setRefs = (node) => {
    dragRef(node);
    dropRef(node);
    combinedRef.current = node;
  };
  return (
    <tr ref={setRefs}>
      <td>
        <div className="common-checkbox no-label margin-auto">
          <input
            onChange={(e) => {
              onCheck(e, folder?._id);
            }}
            name={`check_${i}`}
            id={`check_${i}`}
            type="checkbox"
            checked={state?.selectedIds?.includes(folder?._id)}
          />
          <label htmlFor={`check_${i}`} className="chklabel" />
        </div>
      </td>
      <td onClick={() => enterFolder(folder)}>
        <div className="folder-target d-inline-flex align-items-center">
          <i className="icon-Folder f-24 mr-8" /> {folder?.documentTitle}
        </div>
      </td>
      <td />
      <td />
      <td />
      <td />
      <td />
      {isSubBrandAccess && <td />}
      <td>
        <div className="manual-actions">
          {!isArchived && manualAddEditAccess && (
            <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.EDIT')}>
              <button type="button" aria-label="Edit" className="btn p-0" onClick={() => handleEdit(folder)}>
                <EditIcon size={24} />
              </button>
            </CustomTooltip>
          )}

          {manualDeleteAccess && (
            <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.DELETE')}>
              <button aria-label="button" type="button" className="btn p-0" onClick={() => handleDeleteMultiple(folder?._id)}>
                <DeleteIcon size={24} />
              </button>
            </CustomTooltip>
          )}

          <button aria-label="more" type="button" className="btn p-0">
            <OverlayTrigger
              placement="left"
              trigger="click"
              rootClose
              onToggle={(nextShow) => handleToggle(i, nextShow)}
              overlay={
                <Popover className="table-action-popup">
                  <Popover.Content>
                    <ul>
                      <li>
                        <button className="btn" type="button" aria-label="button" onClick={() => handleFolderDownload(folder)}>
                          <DownloadIcon size={28} /> <span className="text">{t('MANUAL_DOCUMENTS.DOWNLOAD')}</span>
                        </button>
                      </li>
                      {!isArchived && manualAddEditAccess && (
                        <>
                          {document?.type === 'file' && (
                            <li>
                              <button aria-label="button" type="button" className="btn" onClick={() => shareDocumentWithSupplier(folder)}>
                                <ShareIcon size={28} /> <span className="text">{t('MANUAL_DOCUMENTS.SHARE')}</span>
                              </button>
                            </li>
                          )}
                          <li>
                            <button aria-label="Move Document" type="button" className="btn d-inline-flex" onClick={() => moveDocumentToFolder(folder)}>
                              <OverlayTrigger
                                placement="top"
                                trigger="click"
                                rootClose
                                overlay={
                                  <Popover className="custom-popover file-download-popover">
                                    <Popover.Content>
                                      <div className="mb-0 common-react-select" onClick={(e) => e.stopPropagation()}>
                                        <Select
                                          id="folderSelect"
                                          className="common-select"
                                          classNamePrefix="common-select"
                                          options={[
                                            ...(state?.breadCrumbData?.length > 0 ? [{ value: 'home', label: 'Home' }] : []),
                                            ...getAllFolderList
                                              .filter((re) => re?._id !== folder?._id)
                                              .map((fol) => ({
                                                value: fol._id,
                                                label: fol.documentTitle,
                                              })),
                                          ]}
                                          value={
                                            state?.selectedFolder
                                              ? {
                                                  value: state?.selectedFolder,
                                                  label:
                                                    state?.selectedFolder === 'home'
                                                      ? 'HoMe'
                                                      : getAllFolderList.find((fol) => fol._id === state?.selectedFolder)?.documentTitle,
                                                }
                                              : null
                                          }
                                          onChange={(selectedOption) => setSelectedFolder(selectedOption)}
                                          placeholder="Select Folder"
                                        />
                                      </div>
                                    </Popover.Content>
                                  </Popover>
                                }
                              >
                                <div>
                                  <MoveIcon size={28} />
                                  {t('MANUAL_DOCUMENTS.MOVE')}
                                </div>
                              </OverlayTrigger>
                            </button>
                          </li>
                        </>
                      )}
                    </ul>
                  </Popover.Content>
                </Popover>
              }
            >
              <span className="d-inline-flex align-items-center">{openPopoverId === i ? <CloseIcon size={24} /> : <MoreIcon size={24} />}</span>
            </OverlayTrigger>
          </button>
        </div>
      </td>
    </tr>
  );
}

function DocumentTabel({
  handleDeleteMultiple,
  handleArchiveCard,
  onRenew,
  moveDocumentToFolder,
  getDocumentList,
  setFolderOpenModal,
  setOpenModal,
  isArchived,
}) {
  const { state, setState, isallChecked, handleCheckAll, apiCallWithFilter, isSupplierLoggedIn, manualAddEditAccess, manualDeleteAccess, userInfo } =
    useManualState();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const {
    DocumentReducer: { getAllFolderList },
  } = useSelector((reduxState) => reduxState);

  const onCheck = (e, id) => {
    const updatedIds = e.target.checked ? [...state.selectedIds, id] : state?.selectedIds.filter((arrId) => arrId !== id);

    setState((prev) => ({
      ...prev,
      selectedIds: updatedIds,
    }));
  };

  const handleEdit = async (ele) => {
    await dispatch(action.getDocumentDataById(ele?._id));
    if (ele?.type === 'folder') {
      setFolderOpenModal(true);
    } else {
      setOpenModal(true);
    }
    setState((prev) => ({
      ...prev,
      selectedDocumentId: ele?._id,
      isEditFolder: ele.type === 'folder',
      isEditFile: ele.type === 'file',
    }));
  };

  const shareDocumentWithSupplier = async (eleItem) => {
    setState((nextPagestate) => ({
      ...nextPagestate,
      isOpenShareDocPopup: true,
      selectedDocumentId: eleItem,
    }));
  };

  const closeShareDocument = async () => {
    setState((nextPagestate) => ({
      ...nextPagestate,
      isOpenShareDocPopup: false,
      selectedDocumentId: null,
    }));
  };

  const fetchFromS3 = (element) => {
    if (!element) return '';

    // Construct the base URL
    const baseUrl = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${userInfo?.company}/${process.env.REACT_APP_MANUAL_DOCUMENT}`;

    // Split the element path to encode each part separately
    const pathParts = element.split('/');
    const encodedParts = pathParts.map((part) => encodeURIComponent(part));
    const encodedPath = encodedParts.join('/');

    return `${baseUrl}${encodedPath}`;
  };

  const handleDownload = async (element) => {
    if (element?.s3Key) {
      try {
        const s3Url = fetchFromS3(element?.s3Key);

        const response = await fetch(s3Url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Use the sanitized filename for download, fallback to original if needed
        const downloadFileName = element?.fileName || `document_${Date.now()}`;
        link.setAttribute('download', downloadFileName);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(url);

        toasterMsg(t('MANUAL_DOCUMENTS.DOWNLOAD_SUCCESS') || 'File downloaded successfully', 'success');
      } catch (error) {
        toasterMsg(t('MANUAL_DOCUMENTS.DOWNLOAD_ERROR') || 'Error downloading file. Please try again or contact support.', 'danger');
      }
    } else {
      toasterMsg(t('ADD_EDIT_COMPONENET.NO_DOCUMENT_PREVIEW'), 'danger');
    }
  };

  const handleFolderDownload = async (element) => {
    if (element?.type === 'file') {
      handleDownload(element);
    } else {
      const res = await dispatch(action.downloadFolder(element?._id));
      // Create a temporary link element
      const link = document.createElement('a');

      // Generate an object URL for the Blob
      const url = window.URL.createObjectURL(res);

      // Set the download filename
      link.href = url;
      link.download = 'folder.zip'; // You can change the filename here

      // Programmatically click the link to trigger the download
      link.click();

      // Clean up the object URL after the download
      window.URL.revokeObjectURL(url);
    }
  };
  const onPreview = (eleItem) => {
    setState((nextPagestate) => ({
      ...nextPagestate,
      isPreview: true,
      selectedDocumentId: eleItem,
    }));
  };

  const togglePreview = async () => {
    setState((nextPagestate) => ({
      ...nextPagestate,
      isPreview: false,
      selectedDocumentId: null,
    }));
  };

  const closePopup = () => {
    setState((nextPagestate) => ({
      ...nextPagestate,
      selectedFolder: null,
      selectedDocumentId: null,
      isFolderPopup: false,
      selectedIds: [],
    }));
  };

  const setSelectedFolder = async (e) => {
    setState((prev) => ({
      ...prev,
      selectedFolder: e.value,
    }));
    const obj = {
      folderId: e.value === 'home' ? '' : e.value,
      srcId: state?.selectedDocumentId ? state?.selectedDocumentId : state?.selectedIds,
    };

    const res = await dispatch(action.moveDocumentToFolder(obj, t));
    if (res?.code === 200) {
      getDocumentList();
      closePopup();
    }
  };

  const documentList = state.documentList || [];
  let enterFolderTimeout;

  const enterFolder = async (item) => {
    if (enterFolderTimeout || state.isFetching) return; // Prevent duplicate calls
    enterFolderTimeout = setTimeout(() => {
      enterFolderTimeout = null;
    }, 300); // Debounce logic

    setState((prev) => ({
      ...prev,
      breadCrumbData: [],
      searchText: '',
      documentList: [],
      pageIndex: 1,
      isFetching: true,
      selectedDocumentId: null,
      selectedIds: [],
      loading: true,
    }));

    localStorage.setItem('manualDoumentFolder', JSON.stringify(item));
    await dispatch(action.getBreadCrumbDocumentData(item?._id));
    dispatch(action.clearReducer());

    const localstorageFilter = JSON.parse(localStorage.getItem('documentsFilter'));

    if (
      localstorageFilter &&
      (localstorageFilter?.brandId?.length ||
        localstorageFilter?.suppliers?.length ||
        localstorageFilter?.documentFilterType ||
        localstorageFilter?.startDate ||
        localstorageFilter?.endDate)
    ) {
      apiCallWithFilter(localstorageFilter, item?._id || null);
    } else {
      const obj = {
        pageIndex: 1,
        pageSize: state?.pageSize,
        folderId: item._id,
        isArchieve: state?.isArchieve,
      };
      const res = await dispatch(action.getManualDocumentList(obj));
      if (res) {
        setState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    }

    setState((prev) => ({ ...prev, isFetching: false }));
  };

  const onDropDocument = async (droppedItem, targetFolder) => {
    if (targetFolder.type === 'folder') {
      const obj = {
        folderId: targetFolder._id,
        srcId: droppedItem.id,
      };

      await dispatch(action.moveDocumentToFolder(obj, t));
      enterFolder(targetFolder);
    }

    setState((prev) => ({ ...prev, isFetching: false }));
  };

  return (
    <div id="accordion" role="tablist" aria-multiselectable="true">
      <table className="table mb-0 common-custom-table ">
        <thead>
          <tr>
            <th aria-label="check" className="w-50px">
              <div className="common-checkbox margin-auto">
                <input type="checkbox" name="selectAll" id="selectAll" onChange={(e) => handleCheckAll(e)} checked={isallChecked} />

                <label htmlFor="selectAll" className="chklabel" />
              </div>
            </th>

            <th className="w-150px">{t('MANUAL_DOCUMENTS.NAME')}</th>
            <th className="w-150px">{t('MANUAL_DOCUMENTS.SIZE')}</th>
            <th className="w-150px">{t('MANUAL_DOCUMENTS.TYPE')} </th>
            <th className="w-150px">{t('MANUAL_DOCUMENTS.BRAND')}</th>
            {state?.isSubBrandAccess && <th className="w-150px">{t('MANUAL_DOCUMENTS.SUB_BRAND')}</th>}
            <th className="w-150px">{t('MANUAL_DOCUMENTS.SUPPLIER')}</th>
            <th className="w-150px">{t('MANUAL_DOCUMENTS.EXPIRY_DATE')}</th>
            <th className="w-150px" aria-label="action" />
          </tr>
        </thead>
        <tbody>
          {documentList.length > 0 ? (
            documentList.map((document, i) =>
              document.type === 'folder' ? (
                <FolderRow
                  key={document._id}
                  folder={document}
                  onDropDocument={onDropDocument}
                  enterFolder={enterFolder}
                  state={state}
                  i={i}
                  onCheck={onCheck}
                  manualAddEditAccess={manualAddEditAccess}
                  manualDeleteAccess={manualDeleteAccess}
                  isArchived={isArchived}
                  getAllFolderList={getAllFolderList}
                  handleFolderDownload={handleFolderDownload}
                  shareDocumentWithSupplier={shareDocumentWithSupplier}
                  moveDocumentToFolder={moveDocumentToFolder}
                  handleEdit={handleEdit}
                  handleDeleteMultiple={handleDeleteMultiple}
                  setSelectedFolder={setSelectedFolder}
                  isSubBrandAccess={state?.isSubBrandAccess}
                />
              ) : (
                <DocumentRow
                  key={document._id}
                  document={document}
                  onCheck={onCheck}
                  onDropDocument={onDropDocument}
                  handleEdit={handleEdit}
                  handleDeleteMultiple={handleDeleteMultiple}
                  handleArchiveCard={handleArchiveCard}
                  handleDownload={handleDownload}
                  handleFolderDownload={handleFolderDownload}
                  onPreview={onPreview}
                  onRenew={onRenew}
                  shareDocumentWithSupplier={shareDocumentWithSupplier}
                  moveDocumentToFolder={moveDocumentToFolder}
                  setSelectedFolder={setSelectedFolder}
                  isSupplierLoggedIn={isSupplierLoggedIn}
                  manualAddEditAccess={manualAddEditAccess}
                  manualDeleteAccess={manualDeleteAccess}
                  state={state}
                  isArchived={isArchived}
                  getAllFolderList={getAllFolderList}
                  i={i}
                  isSubBrandAccess={state?.isSubBrandAccess}
                />
              ),
            )
          ) : (
            <tr>
              <td colSpan={state?.isSubBrandAccess ? 9 : 8}>
                <p className="text-center m-0">{t('MANUAL_DOCUMENTS.NO_DOCUMENT_ENTRY_FOUND')}</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {state?.isPreview && (
        <PreviewModal isSupplierLevel onOpen={state?.isPreview} data={state?.selectedDocumentId} moduleName="manualDocument" toggle={togglePreview} />
      )}
      {state?.isOpenShareDocPopup && <ShareDocumentModal onOpen={state?.isOpenShareDocPopup} toggle={closeShareDocument} />}
    </div>
  );
}

export default DocumentTabel;
